import { ReactElement } from 'react'
import type { PlanSettingsStateIndicatorType } from '@editor/hooks/useDebouncedPlanSettingsSave'
import PlanSettingsForm from '@editor/pages/Export/components/ExportSettingsForm/components/PlanSettingsForm'
import { AutosaveIndicator } from '@ui2/feedback'
import { Section } from '@ui2/structure'
import { useGetPlanSettings } from '@editorHooks'

interface Props {
  onPlanSettingsChange: (planSettings: PdfPlanSettings) => void
  flushPlanSettings: () => void
  stateIndicatorType: PlanSettingsStateIndicatorType
}

/**
 * "Einstellungen" section with the project's plan settings (format, orientation, scale, font size,
 * plan area origin) — the same auto-saved form as on the plans page, so the preview and the
 * exported plan can be adjusted without leaving the plan view. The debounced save is owned by the
 * page (`useDebouncedPlanSettingsSave`), which also gates "Plan generieren" on it.
 */
const PlanViewPlanSettingsSection = ({
  onPlanSettingsChange,
  flushPlanSettings,
  stateIndicatorType,
}: Props): ReactElement => {
  const { data: planSettings } = useGetPlanSettings()

  return (
    <Section
      title="Einstellungen"
      collapsible
      hideBorderBottom
      titleAdornment={
        <AutosaveIndicator
          isSaving={stateIndicatorType === 'loading'}
          // A failed save is not "saved" — keep it orange (the error itself is shown as a snackbar).
          dirty={stateIndicatorType === 'unsaved' || stateIndicatorType === 'error'}
          onSaveNow={flushPlanSettings}
          data-cy="plan-view-plan-settings-autosave"
        />
      }
    >
      {planSettings && (
        <PlanSettingsForm
          planSettings={planSettings}
          onPlanSettingsChange={onPlanSettingsChange}
          formId="plan-view-plan-settings-form"
        />
      )}
    </Section>
  )
}

export default PlanViewPlanSettingsSection
